import { type OverrideOpenFeatureProvider } from '@/features/flags-dev/openfeature-override-provider';
import { createCustomComparisonMultiProvider } from '@/features/flags/custom-comparison-strategy';
import {
  createFlagEvaluationTelemetryHook,
  DEFAULTS_ONLY_PROVIDER_NAME,
  FLAGSMITH_PROVIDER_NAME,
  OFREP_PROVIDER_NAME,
  registerFlagProviderName,
} from '@/features/flags/flag-evaluation-telemetry';
import { getDomains } from '@/helpers/getDomains';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { getV2StreamHeaders } from '@/utils/http/scribeSseHeaders';
import { logger } from '@/utils/logger/logger';
import { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';
import { OFREPWebProvider } from '@openfeature/ofrep-web-provider';
import { OpenFeature } from '@openfeature/react-sdk';
import {
  type EvaluationContext,
  type EventContext,
  GeneralError,
  InMemoryProvider,
  type JsonValue,
  OpenFeatureEventEmitter,
  type Provider,
  type ProviderEmittableEvents,
  ProviderEvents,
  ProviderNotReadyError,
  type ResolutionDetails,
} from '@openfeature/web-sdk';
import { isEqual } from 'lodash-es';

const FLAGSMITH_INIT_TIMEOUT_MS = 8_000;
// Under the 2s resolveFlagGate bound: readiness waits on both providers.
const OFREP_TIMEOUT_MS = 1_500;
const OFREP_CACHE_TTL_SECONDS = 7 * 24 * 60 * 60;
// Wider than the boot bound: a slow-but-healthy identify during login (proxy
// cold start, Flagsmith deploy) is the likelier false positive, and
// evaluation keeps serving — code defaults at ERROR — while it settles.
const FLAGSMITH_CONTEXT_CHANGE_TIMEOUT_MS = 20_000;

/**
 * Bound a Flagsmith `initialize()` that may never settle: flagsmith-js has no
 * request timeout, and a black-holed connection parks the OpenFeature domain
 * at NOT_READY — a status readiness gates never treat as resolved. (A fetch
 * that *errors* is different: flagsmith swallows it and init resolves.)
 * Rejecting instead lands the domain at ERROR — a settled state consumers
 * fall back from onto code defaults, and one the client-SDK self-report
 * treats as diagnosable.
 *
 * Recovery from ERROR is two-track. VALUES: the still-pending flagsmith fetch
 * completing fires the SDK's onChange, so real flags land and hooks
 * re-render. STATUS: the next successful `setContext` flips the domain back
 * to READY (the SDK's context-change handler sets READY on success, and
 * `OpenFeatureContextSync` re-fires setContext on login and every trait
 * change). Provider-emitted events alone do NOT promote status out of ERROR
 * — the SDK forwards them to listeners without mutating its tracked status.
 */
export function withInitTimeout(
  init: Promise<void>,
  timeoutMs: number,
  operation = 'flagsmith init'
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Observe a late rejection after the bound wins so it is not unhandled.
  init.catch(() => undefined);
  return Promise.race([
    init,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new GeneralError(`${operation} did not settle in ${timeoutMs}ms`)),
        timeoutMs
      );
    }),
  ]).finally(() => clearTimeout(timer));
}

export class BoundedInitFlagsmithProvider extends FlagsmithClientProvider {
  override initialize(
    context?: Parameters<FlagsmithClientProvider['initialize']>[0]
  ): Promise<void> {
    return withInitTimeout(super.initialize(context), FLAGSMITH_INIT_TIMEOUT_MS);
  }

  /**
   * The base delegates `onContextChange` to `this.initialize`, which would
   * inherit the 8s boot bound. Call the UNBOUNDED base initialize under the
   * wider context-change bound instead, replicating the base's Stale
   * emission.
   */
  override onContextChange(
    oldContext: Parameters<FlagsmithClientProvider['onContextChange']>[0],
    newContext: Parameters<FlagsmithClientProvider['onContextChange']>[1]
  ): Promise<void> {
    if (isEqual(oldContext, newContext)) return Promise.resolve();
    this.events.emit(ProviderEvents.Stale, { message: 'Context Changed' });
    return withInitTimeout(
      super.initialize(newContext),
      FLAGSMITH_CONTEXT_CHANGE_TIMEOUT_MS,
      'flagsmith context change'
    );
  }
}

type OfrepSnapshot = 'none' | 'pending' | 'ready' | 'failed';

const OFREP_STORAGE_PREFIX = 'ofrep-web-provider:';

const isLiveOfrepEntry = (raw: string | null): boolean => {
  try {
    const writtenAt = Date.parse(
      (JSON.parse(raw ?? '') as { writtenAt?: string }).writtenAt ?? ''
    );
    return Date.now() - writtenAt < OFREP_CACHE_TTL_SECONDS * 1000;
  } catch {
    return false;
  }
};

// The library only evicts an entry when the same identity reads it again.
const pruneOfrepCache = (dropAll: boolean): void => {
  try {
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(OFREP_STORAGE_PREFIX)) continue;
      if (dropAll || !isLiveOfrepEntry(localStorage.getItem(key)))
        localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing persisted to prune.
  }
};

// The shadow provider must never degrade the serving provider's status.
class ShadowEventEmitter extends OpenFeatureEventEmitter {
  override emit(event: ProviderEmittableEvents, context?: EventContext): void {
    if (event === ProviderEvents.Stale || event === ProviderEvents.Error) {
      logger.warn('[flags] OFREP unavailable', { failure: 'refresh_failed' });
      return;
    }
    super.emit(event, context);
  }
}

export class OfrepProvider extends OFREPWebProvider {
  override readonly events = new ShadowEventEmitter();
  private boundDomain?: string;
  private requestedKey?: string;
  private snapshot: OfrepSnapshot = 'none';
  private queue: Promise<void> = Promise.resolve();

  override initialize(context?: EvaluationContext, domain?: string): Promise<void> {
    this.boundDomain = domain;
    return context?.targetingKey ? this.enqueue(context.targetingKey) : Promise.resolve();
  }

  override onContextChange(
    _oldContext: EvaluationContext,
    newContext: EvaluationContext
  ): Promise<void> {
    return this.enqueue(newContext.targetingKey);
  }

  override resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    context: EvaluationContext
  ): ResolutionDetails<boolean> {
    this.assertSnapshot();
    return super.resolveBooleanEvaluation(flagKey, defaultValue, context);
  }

  override resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    context: EvaluationContext
  ): ResolutionDetails<string> {
    this.assertSnapshot();
    return super.resolveStringEvaluation(flagKey, defaultValue, context);
  }

  override resolveNumberEvaluation(
    flagKey: string,
    defaultValue: number,
    context: EvaluationContext
  ): ResolutionDetails<number> {
    this.assertSnapshot();
    return super.resolveNumberEvaluation(flagKey, defaultValue, context);
  }

  override resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
    context: EvaluationContext
  ): ResolutionDetails<T> {
    this.assertSnapshot();
    return super.resolveObjectEvaluation(flagKey, defaultValue, context);
  }

  readonly fetchForCurrentIdentity = async (
    ...[input, init]: Parameters<typeof fetch>
  ): Promise<Response> => {
    const targetingKey = this.requestedKey;
    const response = await fetch(input, init);
    if (targetingKey === this.requestedKey) return response;
    void response.body?.cancel();
    logger.warn('[flags] OFREP refresh discarded', { reason: 'identity_changed' });
    // A 304 makes the SDK keep the current identity's flags instead of applying a stale refresh.
    return new Response(null, { status: 304 });
  };

  private enqueue(targetingKey?: string): Promise<void> {
    this.queue = this.queue.then(() => this.evaluate(targetingKey));
    return this.queue;
  }

  private async evaluate(targetingKey?: string): Promise<void> {
    if (this.snapshot === 'ready' && this.requestedKey === targetingKey) return;
    pruneOfrepCache(
      this.requestedKey !== undefined && this.requestedKey !== targetingKey
    );
    this.requestedKey = targetingKey;
    this.snapshot = 'pending';
    try {
      await super.initialize(targetingKey ? { targetingKey } : {}, this.boundDomain);
      this.snapshot = 'ready';
    } catch {
      this.snapshot = 'failed';
      logger.warn('[flags] OFREP unavailable', { failure: 'request_failed' });
    }
  }

  private assertSnapshot(): void {
    if (this.snapshot === 'ready') return;
    if (this.snapshot === 'failed') throw new GeneralError('OFREP unavailable');
    throw new ProviderNotReadyError('OFREP has no snapshot yet');
  }
}

// Everything the request sends except the rotating token: workspace, client and temp user.
const ofrepCacheScope = () =>
  Object.entries(getV2StreamHeaders())
    .filter(([name]) => name.toLowerCase() !== 'authorization')
    .sort(([a], [b]) => a.localeCompare(b));

export const createOfrepProvider = (): Provider => {
  const provider: OfrepProvider = new OfrepProvider({
    get baseUrl() {
      return `${getDomains().baseV2Url}feature-control`;
    },
    headersFactory: () => Promise.resolve(Object.entries(getV2StreamHeaders())),
    fetchImplementation: (...args) => provider.fetchForCurrentIdentity(...args),
    cacheMode: 'local-cache-first',
    cacheTTL: OFREP_CACHE_TTL_SECONDS,
    cacheKeyGenerator: ({ url, domain, targetingKey }) =>
      JSON.stringify([url, domain ?? '', targetingKey ?? '', ofrepCacheScope()]),
    timeoutMs: OFREP_TIMEOUT_MS,
  });
  return provider;
};

/**
 * Cloud flag-provider registration — app wiring for the
 * `@heidi/runtime/feature-flags` door (P3 F3; design D4/D9). The door exposes
 * NO registration API: this module is where providers are chosen,
 * constructed, and installed, so a build variant's disabled path is ABSENCE,
 * not a runtime check inside the package. On-prem flag evaluation is fed by
 * the in-process bundle provider instead (`onprem-bundle-provider.tsx`,
 * mounted via `OnPremFlagBundleSync`).
 *
 * Called synchronously from `_app.tsx` module scope (browser only). Provider
 * install must NOT be deferred/async — boot-gated consumers (e.g.
 * ThemeManager) read flags on first paint, and an async install would race
 * them to the code default.
 */
export function registerFlagProviders(options: {
  /**
   * Dev-tools wrapper class (non-production builds only — `_app.tsx` loads it
   * behind its env-gated conditional `require`, so it is `undefined` in
   * production). When present, wraps the Flagsmith provider so sessionStorage
   * overrides intercept resolve* calls and re-emit ConfigurationChanged for
   * live `use*FlagValue` hooks.
   */
  overrideOpenFeatureProvider?: typeof OverrideOpenFeatureProvider;
  ofrepEnabled?: boolean;
}): void {
  // TELEMETRY-GATE (onprem-guardrails F1): the gate is the belt — key PRESENCE
  // alone must never construct or initialize a phone-home SDK (a leaked
  // NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY would otherwise point the Flagsmith
  // SDK at the sys.heidihealth.com proxy).
  //
  // This reads the telemetry axis rather than `isOnPrem` because the S5 egress
  // smoke caught it: with a key present, a cloud build with telemetry off still
  // reached `sys.heidihealth.com/fs/api/v1/flags/`. "Degrades to code defaults
  // via key absence" is belt without braces, which is what F1 forbids. Flags
  // resolving to code defaults on a suppressed build is the same posture the
  // axis already gives PostHog-sourced flags (design D6).
  //
  // Scope: construct/initialize — the static imports above stay (install must
  // be synchronous, see the doc block); bundle-level removal of the
  // loaded-but-inert SDK modules is the separately tracked ARCFE-664
  // `KNOWN_LEAKS` concern.
  if (!isSaasTelemetryEnabled) return;

  OpenFeature.addHooks(createFlagEvaluationTelemetryHook());

  // Initialize OpenFeature with Flagsmith provider
  const flagsmithEnvKey = process.env.NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY;
  if (flagsmithEnvKey) {
    const flagsmithClientProvider = new BoundedInitFlagsmithProvider({
      environmentID: flagsmithEnvKey,
      api: 'https://sys.heidihealth.com/fs/api/v1/',
      cacheFlags: true,
      cacheOptions: {
        skipAPI: false,
      },
    });
    registerFlagProviderName(undefined, FLAGSMITH_PROVIDER_NAME);

    const provider: Provider = options.ofrepEnabled
      ? createCustomComparisonMultiProvider({
          primary: { provider: flagsmithClientProvider, name: FLAGSMITH_PROVIDER_NAME },
          secondary: { provider: createOfrepProvider(), name: OFREP_PROVIDER_NAME },
        })
      : flagsmithClientProvider;

    if (options.overrideOpenFeatureProvider) {
      // Wrap the real provider so dev-tools overrides intercept resolve*
      // calls and re-emit ConfigurationChanged for live `use*FlagValue`
      // hooks. Must install synchronously — async loading races boot-gated
      // consumers like ThemeManager that read flags on first paint.
      try {
        OpenFeature.setProvider(
          new options.overrideOpenFeatureProvider(provider, 'flagsmith')
        );
      } catch {
        logger.warn('[flags-dev] failed to install override provider', {
          failure: 'registration_failed',
        });
        OpenFeature.setProvider(provider);
      }
    } else {
      OpenFeature.setProvider(provider);
    }
  } else {
    // No flag key on a telemetry-enabled build. A defaults-only provider is
    // READY at once, so the readiness gates resolve and every flag serves its
    // call-site default; no SDK is constructed. Named and logged so a deployed
    // build that lost its key shows up on the flag-evaluation dashboard.
    logger.warn('[flags] no flag key configured; serving call-site defaults', {
      failure: 'no_provider_key',
    });
    registerFlagProviderName(undefined, DEFAULTS_ONLY_PROVIDER_NAME);
    OpenFeature.setProvider(new InMemoryProvider({}));
  }
}
